import React, { useEffect, useState, useCallback, useMemo } from "react";
import { getCustomization } from "./customization";
import MiniTableProCore from "./MiniTableProCore";
import EditTableButtonsDialog from "./EditTableButtonsDialog";
import ReorderTutorial from "./ReorderTutorial";
import AddPropertyFromListDialog from "./AddPropertyFromListDialog";
import SinglePropertyAddEditDialog from "./SinglePropertyAddEditDialog";
import DeletePropertyDialog from "./DeletePropertyDialog";
import EditPropertyDialog from "./EditPropertyDialog";
import ReorderPropertyDialog from "./ReorderPropertyDialog";
import EditHeaderTextDialog from "./EditHeaderTextDialog";
import EditTableFooterDialog from "./EditTableFooterDialog";
import PropertyUrlViewDialog from "views/Pma/MailPro/components/PropertyUrlViewDialog";
import { getAreaTitleText } from "./EditTableButtonsDialog";
import { UpdateMiniTableDetails } from "services/miniTablePro";
import { getPropertyId, getCombinedPropertiesFromQuarterly } from "./utils";

/**
 * Create a placeholder property row for "Add" (local store only).
 */
function createPlaceholderProperty(_sortingOrder = 0) {
  return {
    address: "-",
    streetDirPrefix: null,
    streetNumber: null,
    _isShowStreetNumber: false,
    streetName: null,
    streetSuffix: null,
    status: "Active",
    listPrice: "-",
    salePrice: "-",
    listDate: "-",
    saleDate: "-",
    daysOnMarket: "-",
    bedBaths: "-",
    squareFeet: "-",
    propertyType: "Single Family Home",
    _sortingOrder,
  };
}

function escapeHtml(text) {
  return String(text ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function buildMiniTableTitleHtml(titleText) {
  const safeText = escapeHtml(titleText).trim();
  return safeText ? `<p class="small-left-subtitle">${safeText}</p>` : "";
}

function buildMiniTableSubTitleHtml(subtitlePart1, subtitlePart2) {
  const safePart1 = escapeHtml(subtitlePart1).trim();
  const safePart2 = escapeHtml(subtitlePart2).trim();
  if (safePart1 && safePart2) {
    return `<p>${safePart1} — <span class="color-blue">${safePart2}</span></p>`;
  }
  if (safePart2) {
    return `<p>— <span class="color-blue">${safePart2}</span></p>`;
  }
  if (safePart1) {
    return `<p>${safePart1}</p>`;
  }
  return "";
}

/**
 * Editor version. All edit-related state and UI: reorder, edit, delete, add, dialogs.
 */
function MiniTableProEditor({ printVersion = false, maxRows, showAllEditButtons, ...props }) {
  const {
    miniTableProperties,
    deletedMiniTableProperties = [],
    onEditTable,
    onOrderChange,
    onHeaderTextUpdated,
    onFooterTextUpdated,
    ...rest
  } = props;


  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addPropertyDialogOpen, setAddPropertyDialogOpen] = useState(false);
  const [addPropertyRowIndex, setAddPropertyRowIndex] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editPropertyDialogOpen, setEditPropertyDialogOpen] = useState(false);
  const [reorderDialogOpen, setReorderDialogOpen] = useState(false);
  const [showReorderTutorial, setShowReorderTutorial] = useState(false);
  const [viewEntirePropertyListMode, setViewEntirePropertyListMode] = useState(false);
  const [deletedProperties, setDeletedProperties] = useState(deletedMiniTableProperties ?? []);
  const [propertyEditDialogOpen, setPropertyEditDialogOpen] = useState(false);
  const [editHeaderDialogOpen, setEditHeaderDialogOpen] = useState(false);
  const [isSavingHeaderText, setIsSavingHeaderText] = useState(false);
  const [editFooterDialogOpen, setEditFooterDialogOpen] = useState(false);
  const [isSavingFooterText, setIsSavingFooterText] = useState(false);
  const [propertyDetailsDialogOpen, setPropertyDetailsDialogOpen] = useState(false);
  const [selectedPropertyForDetails, setSelectedPropertyForDetails] = useState(null);
  const [selectedPropertyForEdit, setSelectedPropertyForEdit] = useState(null);
  const [selectedPropertyIndexForEdit, setSelectedPropertyIndexForEdit] = useState(null);
  const [isManualAddMode, setIsManualAddMode] = useState(false);
  const [localMiniTableTitle, setLocalMiniTableTitle] = useState(rest.miniTableTitle || "");
  const [localMiniTableSubTitle, setLocalMiniTableSubTitle] = useState(rest.miniTableSubTitle || "");
  const [localMiniTableDisclaimer2, setLocalMiniTableDisclaimer2] = useState(rest.miniTableDisclaimer2 || "");
  const [localMiniTableDisclaimer3, setLocalMiniTableDisclaimer3] = useState(rest.miniTableDisclaimer3 || "");
  const [localMiniTableDisclaimer4, setLocalMiniTableDisclaimer4] = useState(rest.miniTableDisclaimer4 || "");
  const [localSorted, setLocalSorted] = useState(() =>
    (miniTableProperties || []).map((item, i) => ({
      ...item,
      // _sortingOrder is zero-based and starts from 0.
      _sortingOrder:
        item._sortingOrder !== undefined && item._sortingOrder !== null
          ? item._sortingOrder
          : i,
    }))
  );

  useEffect(() => {
    require("./style.scss");
  }, []);

  useEffect(() => {
    setLocalSorted(
      (miniTableProperties || []).map((item, i) => ({
        ...item,
        _sortingOrder:
          item._sortingOrder !== undefined && item._sortingOrder !== null
            ? item._sortingOrder
            : i,
      }))
    );
  }, [miniTableProperties]);

  useEffect(() => {
    setDeletedProperties(deletedMiniTableProperties ?? []);
  }, [rest.miniTableDetailsID, deletedMiniTableProperties]);

  useEffect(() => {
    setLocalMiniTableTitle(rest.miniTableTitle || "");
    setLocalMiniTableSubTitle(rest.miniTableSubTitle || "");
  }, [rest.miniTableDetailsID, rest.miniTableTitle, rest.miniTableSubTitle]);

  useEffect(() => {
    setLocalMiniTableDisclaimer2(rest.miniTableDisclaimer2 || "");
    setLocalMiniTableDisclaimer3(rest.miniTableDisclaimer3 || "");
    setLocalMiniTableDisclaimer4(rest.miniTableDisclaimer4 || "");
  }, [rest.miniTableDetailsID, rest.miniTableDisclaimer2, rest.miniTableDisclaimer3, rest.miniTableDisclaimer4]);

  const sortedByOrder = useMemo(
    () =>
      [...(localSorted || [])].sort(
        (a, b) => (Number(a._sortingOrder) ?? 0) - (Number(b._sortingOrder) ?? 0)
      ),
    [localSorted]
  );

  const miniTableMeta = useMemo(
    () => ({
      miniTableDetailsID: rest.miniTableDetailsID,
      miniTableMaxRows: rest.miniTableMaxRows,
      mlsAreaID: rest.currentArea?.mlsAreaID,
      miniTableTitle: localMiniTableTitle,
      miniTableSubTitle: localMiniTableSubTitle,
      miniTableDisclaimer1: rest.miniTableDisclaimer1,
      miniTableDisclaimer2: localMiniTableDisclaimer2,
      miniTableDisclaimer3: localMiniTableDisclaimer3,
      miniTableDisclaimer4: localMiniTableDisclaimer4,
      miniTableDateRange: rest.miniTableDateRange,
      miniTableAddedDate: rest.miniTableAddedDate,
      miniTableUpdatedDate: rest.miniTableUpdatedDate,
      miniTableDoNotUpdate: rest.miniTableDoNotUpdate,
      miniTableStartDate: rest.miniTableStartDate,
    }),
    [
      rest.miniTableDetailsID,
      rest.miniTableMaxRows,
      rest.currentArea?.mlsAreaID,
      localMiniTableTitle,
      localMiniTableSubTitle,
      rest.miniTableDisclaimer1,
      localMiniTableDisclaimer2,
      localMiniTableDisclaimer3,
      localMiniTableDisclaimer4,
      rest.miniTableDateRange,
      rest.miniTableAddedDate,
      rest.miniTableUpdatedDate,
      rest.miniTableDoNotUpdate,
      rest.miniTableStartDate,
    ]
  );

  const handleAddProperty = useCallback(() => {
    setSelectedPropertyForEdit(null);
    setSelectedPropertyIndexForEdit(null);
    setAddPropertyRowIndex(null);
    setAddPropertyDialogOpen(true);
  }, []);

  const handleDeleteProperty = useCallback((deletedIndices) => {
    // NOTE: Delete API expects full miniTable payload with _isDeleted flags.
    // Selected rows => _isDeleted: true, all others => _isDeleted: false.
    const sortedIndices = [...deletedIndices].sort((a, b) => b - a);
    const deletedIndexSet = new Set(sortedIndices);
    const flaggedForDelete = sortedByOrder.map((item, i) => ({
      ...item,
      _isDeleted: deletedIndexSet.has(i),
    }));
    const toDelete = flaggedForDelete.filter((item) => item._isDeleted);

    UpdateMiniTableDetails({
      ...miniTableMeta,
      miniTable: flaggedForDelete,
    })
      .then(() => {
        setDeletedProperties((prevDeleted) => {
          const existingIds = new Set(
            prevDeleted.map((p) => getPropertyId(p)).filter(Boolean)
          );
          const newDeleted = toDelete.filter(
            (p) => !p._isAdded && !p._isManuallyAdded
          );
          const uniqueNewDeleted = newDeleted.filter(
            (p) => !existingIds.has(getPropertyId(p))
          );
          return [...prevDeleted, ...uniqueNewDeleted];
        });

        // NOTE: After successful API response, keep only non-deleted rows in local table.
        // Also clear _isDeleted locally and normalize _sortingOrder.
        const updated = flaggedForDelete
          .filter((item) => !item._isDeleted)
          .map((item, i) => ({ ...item, _isDeleted: false, _sortingOrder: i }));
        setLocalSorted(updated);
        onOrderChange?.(updated);
      })
      .catch(() => {});
  }, [sortedByOrder, onOrderChange, miniTableMeta]);

  const handleEditProperty = useCallback((index, updated) => {
    setLocalSorted((prev) => {
      if (index < 0 || index >= prev.length) return prev;
      const next = [...prev];
      next[index] = { ...next[index], ...updated, _isEdited: true };
      return next;
    });
  }, []);

  const handleEditPropertyClick = useCallback((property, index) => {
    setSelectedPropertyForEdit(property);
    setSelectedPropertyIndexForEdit(index);
    setPropertyEditDialogOpen(true);
  }, []);

  const handleViewPropertyDetails = useCallback((property) => {
    setSelectedPropertyForDetails(property);
    setPropertyDetailsDialogOpen(true);
  }, []);

  const handlePropertyEditSave = useCallback((updatedProperty) => {
    if (selectedPropertyIndexForEdit !== null && selectedPropertyIndexForEdit >= 0) {
      // Edit: build full list with form keys at index, call same update API as reorder/delete
      const newList = [...sortedByOrder];
      const existing = newList[selectedPropertyIndexForEdit];
      newList[selectedPropertyIndexForEdit] = { ...existing, ...updatedProperty };
      UpdateMiniTableDetails({
        ...miniTableMeta,
        miniTable: newList,
      })
        .then(() => {
          setLocalSorted(newList);
          onOrderChange?.(newList);
          setPropertyEditDialogOpen(false);
          setSelectedPropertyForEdit(null);
          setSelectedPropertyIndexForEdit(null);
          if (isManualAddMode) {
            setIsManualAddMode(false);
            setAddPropertyDialogOpen(true);
          }
        })
        .catch(() => {});
    } else {
      // Add: dialog already called AddMiniTableProperty; just update local state
      if (sortedByOrder.length >= maxRows) {
        setPropertyEditDialogOpen(false);
        setSelectedPropertyForEdit(null);
        setSelectedPropertyIndexForEdit(null);
        return;
      }
      const newList = [
        ...sortedByOrder,
        { ...updatedProperty, _sortingOrder: sortedByOrder.length },
      ];
      setLocalSorted(newList);
      onOrderChange?.(newList);
      setPropertyEditDialogOpen(false);
      setSelectedPropertyForEdit(null);
      setSelectedPropertyIndexForEdit(null);
      if (isManualAddMode) {
        setIsManualAddMode(false);
        setAddPropertyDialogOpen(true);
      }
    }
  }, [selectedPropertyIndexForEdit, sortedByOrder, miniTableMeta, isManualAddMode, maxRows, onOrderChange]);

  const handleReorder = useCallback(
    (newItemsWithSortingOrder) => {
      setLocalSorted(newItemsWithSortingOrder);
      onOrderChange?.(newItemsWithSortingOrder);
    },
    [onOrderChange]
  );

  const handleAddToRow = useCallback((rowIndex) => {
    setAddPropertyRowIndex(rowIndex);
    setAddPropertyDialogOpen(true);
  }, []);

  const handleAddPropertiesFromList = useCallback((propertiesToAdd) => {
    const withAddedFlag = propertiesToAdd.map((p) => ({ ...p, _isAdded: true }));
    const updated = [...sortedByOrder, ...withAddedFlag].map((item, i) => ({
      ...item,
      _sortingOrder: i,
      //We need to reset the _isDeleted flag for the properties that were added to the table
      _isDeleted: false,
    }));

    UpdateMiniTableDetails({
      ...miniTableMeta,
      miniTable: updated,
    })
      .then(() => {
        setLocalSorted(updated);
        onOrderChange?.(updated);
        setDeletedProperties((prev) => {
          //Remove properties that were added back to the table
          const addedBackIds = new Set(propertiesToAdd.map((p) => getPropertyId(p)).filter(Boolean));
          return prev.filter((p) => !addedBackIds.has(getPropertyId(p)));
        });
      })
      .catch(() => {});
  }, [sortedByOrder, miniTableMeta, onOrderChange]);

  const handleManualAddProperty = useCallback(() => {
    setIsManualAddMode(true);
    setSelectedPropertyForEdit(null);
    setSelectedPropertyIndexForEdit(null);
    setPropertyEditDialogOpen(true);
  }, []);

  const handleHeaderTextSave = useCallback(
    ({ titleText, subtitlePart1, subtitlePart2 }) => {
      if (isSavingHeaderText) return;
      const nextTitleHtml = buildMiniTableTitleHtml(titleText);
      const nextSubTitleHtml = buildMiniTableSubTitleHtml(subtitlePart1, subtitlePart2);
      setIsSavingHeaderText(true);
      UpdateMiniTableDetails({
        ...miniTableMeta,
        miniTableTitle: nextTitleHtml,
        miniTableSubTitle: nextSubTitleHtml,
        miniTable: sortedByOrder,
      })
        .then(() => {
          setLocalMiniTableTitle(nextTitleHtml);
          setLocalMiniTableSubTitle(nextSubTitleHtml);
          onHeaderTextUpdated?.({
            miniTableTitle: nextTitleHtml,
            miniTableSubTitle: nextSubTitleHtml,
          });
          setEditHeaderDialogOpen(false);
        })
        .catch(() => {})
        .finally(() => setIsSavingHeaderText(false));
    },
    [isSavingHeaderText, miniTableMeta, onHeaderTextUpdated, sortedByOrder]
  );

  const handleFooterTextSave = useCallback(
    ({ miniTableDisclaimer2: nextDisclaimer2, miniTableDisclaimer3: nextDisclaimer3, miniTableDisclaimer4: nextDisclaimer4 }) => {
      if (isSavingFooterText) return;
      setIsSavingFooterText(true);
      UpdateMiniTableDetails({
        ...miniTableMeta,
        miniTableDisclaimer2: nextDisclaimer2,
        miniTableDisclaimer3: nextDisclaimer3,
        miniTableDisclaimer4: nextDisclaimer4,
        miniTable: sortedByOrder,
      })
        .then(() => {
          setLocalMiniTableDisclaimer2(nextDisclaimer2 ?? "");
          setLocalMiniTableDisclaimer3(nextDisclaimer3 ?? "");
          setLocalMiniTableDisclaimer4(nextDisclaimer4 ?? "");
          onFooterTextUpdated?.({
            miniTableDisclaimer2: nextDisclaimer2,
            miniTableDisclaimer3: nextDisclaimer3,
            miniTableDisclaimer4: nextDisclaimer4,
          });
          setEditFooterDialogOpen(false);
        })
        .catch(() => {})
        .finally(() => setIsSavingFooterText(false));
    },
    [isSavingFooterText, miniTableMeta, onFooterTextUpdated, sortedByOrder]
  );

  // Combined list from all 7 property keys on quarterlyAreaTable
  const combinedAvailableProperties = useMemo(
    () => getCombinedPropertiesFromQuarterly(rest.quarterlyAreaTable),
    [rest.quarterlyAreaTable]
  );

  // Exclude properties already in the table or in deleted list (by listingID)
  const availablePropertiesFiltered = useMemo(() => {
    const idsInTableOrDeleted = new Set();
    for (const p of [...(sortedByOrder || []), ...(deletedProperties || [])]) {
      if (!p) continue;
      const id = getPropertyId(p);
      if (id) idsInTableOrDeleted.add(id);
    }
    return (combinedAvailableProperties || []).filter((p) => {
      if (!p) return false;
      const id = getPropertyId(p);
      return id != null && !idsInTableOrDeleted.has(id);
    });
  }, [combinedAvailableProperties, sortedByOrder, deletedProperties]);

  const custom = getCustomization({
    ...rest,
    miniTableProperties: sortedByOrder,
    currentSoldsLength: sortedByOrder?.length,
  });
  const { titleConfig } = custom;

  const selectedMailProRecord = rest.mailProPropertyByListingId?.get(
    getPropertyId(selectedPropertyForDetails)
  );

  const renderBeforeTable = !printVersion && showAllEditButtons ? (
    <div className="editor-table-actions">
      <button
        type="button"
        className="click-to-edit-table-button"
        onClick={() => setEditHeaderDialogOpen(true)}
      >
        EDIT HEADER TEXT
      </button>
      <button
        type="button"
        className="click-to-edit-table-button"
        style={{ marginTop: "30px" }}
        onClick={() => {
          setEditDialogOpen(true);
          onEditTable?.();
        }}
      >
        EDIT TABLE
      </button>
    </div>
  ) : null;

  return (
    <>
      <MiniTableProCore
        {...rest}
        miniTableProperties={sortedByOrder}
        printVersion={printVersion}
        renderBeforeTable={renderBeforeTable}
        reorderable={false}
        onReorder={handleReorder}
        onDragStart={() => setShowReorderTutorial(false)}
        deletable={false}
        onDelete={handleDeleteProperty}
        onAddToRow={handleAddToRow}
        maxRows={maxRows}
        editMode={false}
        onEditPropertyClick={handleEditPropertyClick}
        onViewPropertyDetails={handleViewPropertyDetails}
      />
      <EditTableButtonsDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        titleConfig={titleConfig}
        propertyCount={sortedByOrder.length}
        maxProperties={maxRows}
        onAddProperty={handleAddProperty}
        onViewEntirePropertyList={() => {
          setViewEntirePropertyListMode(true);
          setAddPropertyDialogOpen(true);
          setEditDialogOpen(false);
        }}
        onDeleteProperty={() => {
          setViewEntirePropertyListMode(false);
          setDeleteDialogOpen(true);
          setEditDialogOpen(false);
        }}
        onEditProperty={() => {
          setViewEntirePropertyListMode(false);
          setEditPropertyDialogOpen(true);
          setEditDialogOpen(false);
        }}
        onReorderProperties={() => {
          setViewEntirePropertyListMode(false);
          setReorderDialogOpen(true);
          setEditPropertyDialogOpen(false);
          setDeleteDialogOpen(false);
          setEditDialogOpen(false);
          setShowReorderTutorial(true);
        }}
        onEditTableFooter={() => {
          setEditFooterDialogOpen(true);
          setEditDialogOpen(false);
        }}
      />
      <ReorderTutorial
        open={showReorderTutorial}
        onClose={() => setShowReorderTutorial(false)}
      />
      <AddPropertyFromListDialog
        open={addPropertyDialogOpen}
        onClose={() => {
          setAddPropertyDialogOpen(false);
          setViewEntirePropertyListMode(false);
          setAddPropertyRowIndex(null);
        }}
        titleConfig={titleConfig}
        currentProperties={sortedByOrder}
        availableProperties={availablePropertiesFiltered}
        deletedProperties={deletedProperties}
        manuallyAddedProperties={sortedByOrder.filter((p) => p._isManuallyAdded === true)}
        maxProperties={maxRows}
        onAddProperties={handleAddPropertiesFromList}
        onManualAdd={handleManualAddProperty}
        viewOnly={viewEntirePropertyListMode}
        currentArea={rest.currentArea}
        quarterlyAreaTable={rest.quarterlyAreaTable}
        agentData={rest.agentData}
        onViewPropertyDetails={handleViewPropertyDetails}
        mailProPropertyByListingId={rest.mailProPropertyByListingId}
        marketSaleRowProps={{
          mlsAreaID: rest.currentArea?.mlsAreaID,
          isSingleTable: true,
          agentData: rest.agentData,
          hidePropertyTypeColumn: false,
          hasAllActiveProperties: false,
        }}
      />
      <DeletePropertyDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        areaName={getAreaTitleText(titleConfig) || ""}
        properties={sortedByOrder}
        onDelete={handleDeleteProperty}
        maxRows={maxRows}
        currentArea={rest.currentArea}
        quarterlyAreaTable={rest.quarterlyAreaTable}
        agentData={rest.agentData}
      />
      <EditPropertyDialog
        open={editPropertyDialogOpen}
        onClose={() => setEditPropertyDialogOpen(false)}
        areaName={getAreaTitleText(titleConfig) || ""}
        properties={sortedByOrder}
        onEditPropertyClick={handleEditPropertyClick}
        maxRows={maxRows}
        currentArea={rest.currentArea}
        quarterlyAreaTable={rest.quarterlyAreaTable}
        agentData={rest.agentData}
      />
      <ReorderPropertyDialog
        open={reorderDialogOpen}
        onClose={() => setReorderDialogOpen(false)}
        areaName={getAreaTitleText(titleConfig) || ""}
        properties={sortedByOrder}
        onReorder={handleReorder}
        onDragStart={() => setShowReorderTutorial(false)}
        onOpenHowToReorder={() => setShowReorderTutorial(true)}
        onOrderChange={onOrderChange}
        maxRows={maxRows}
        listgenUserID={rest.agentData?.listgenUserID}
        listgenUserAreaID={rest.currentArea?.listgenUserAreaID}
        miniTableMeta={miniTableMeta}
        currentArea={rest.currentArea}
        quarterlyAreaTable={rest.quarterlyAreaTable}
        agentData={rest.agentData}
      />
      <SinglePropertyAddEditDialog
        open={propertyEditDialogOpen}
        onClose={() => {
          setPropertyEditDialogOpen(false);
          setSelectedPropertyForEdit(null);
          setSelectedPropertyIndexForEdit(null);
          // If canceling manual add, reopen list dialog
          if (isManualAddMode) {
            setIsManualAddMode(false);
            setAddPropertyDialogOpen(true);
          }
        }}
        areaName={getAreaTitleText(titleConfig) || ""}
        property={selectedPropertyForEdit}
        agentData={rest.agentData}
        mlsAreaID={rest.currentArea?.mlsAreaID}
        listgenUserAreaID={rest.currentArea?.listgenUserAreaID}
        miniTableMeta={miniTableMeta}
        onSave={handlePropertyEditSave}
        marketSaleRowProps={{
          mlsAreaID: rest.currentArea?.mlsAreaID,
          isSingleTable: true,
          agentData: rest.agentData,
          hidePropertyTypeColumn: false,
          hasAllActiveProperties: false,
        }}
      />
      <EditHeaderTextDialog
        open={editHeaderDialogOpen}
        onClose={() => {
          if (!isSavingHeaderText) setEditHeaderDialogOpen(false);
        }}
        areaName={getAreaTitleText(titleConfig) || ""}
        miniTableTitle={localMiniTableTitle}
        miniTableSubTitle={localMiniTableSubTitle}
        onSave={handleHeaderTextSave}
        isSaving={isSavingHeaderText}
      />
      <EditTableFooterDialog
        open={editFooterDialogOpen}
        onClose={() => {
          if (!isSavingFooterText) setEditFooterDialogOpen(false);
        }}
        areaName={getAreaTitleText(titleConfig) || ""}
        miniTableDisclaimer1={rest.miniTableDisclaimer1}
        miniTableDisclaimer2={localMiniTableDisclaimer2}
        miniTableDisclaimer3={localMiniTableDisclaimer3}
        miniTableDisclaimer4={localMiniTableDisclaimer4}
        offMarketDisclaimerBool={rest.offMarketDisclaimerBool}
        coListingDisclaimerBool={rest.coListingDisclaimerBool}
        onSave={handleFooterTextSave}
        isSaving={isSavingFooterText}
      />
      <PropertyUrlViewDialog
        hideTrigger
        externalOpen={propertyDetailsDialogOpen}
        onExternalClose={() => setPropertyDetailsDialogOpen(false)}
        property={selectedMailProRecord}
        propertyDetails={selectedMailProRecord?.propertyDetails}
        mailingDetails={selectedMailProRecord?.mailingDetails ?? {}}
        agentData={rest.agentData}
      />
    </>
  );
}

export default MiniTableProEditor;
